import { Card, CardContent } from '@talentmesh/ui/components/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@talentmesh/ui/components/tabs';
import { generatePath, Link } from 'react-router-dom';
import IntegrationsTab from '../AccountSettingsTabs/IntegrationsTab/IntegrationsTab';
import PaymentTab from '../AccountSettingsTabs/PaymentTab/PaymentTab';
import ProfileInfo from '../AccountSettingsTabs/ProfileInfoTab/ProfileInfo';
import TeamMembersTab from '../AccountSettingsTabs/TeamMembersTab/TeamMembersTab';
import AccountNotifications from './AccountNotification/AccountNotifications';
import CompanyProfileTab from './CompanyProfileTab/CompanyProfileTab';
import EditEmailTemplate from './EmailTemplate/EditEmailTemplateTab';
import useCompanyId from '@/Hooks/UseCompanyId';
import useRouteMatch from '@/Hooks/useRouteMatch';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';

function AccountSettingsTabs() {
    const companyId = useCompanyId();

    const routeMatch = useRouteMatch([
        RoutePath.AccountSettingsGeneral,
        RoutePath.AccountSettingsCompanyProfile,
        RoutePath.AccountSettingsNotifications,
        RoutePath.AccountSettingsPayment,
        RoutePath.AccountSettingsEmailTemplates,
        RoutePath.AccountSettingsTeamMembers,
        RoutePath.AccountSettingsIntegrations,
    ]);

    const currentTab = routeMatch || RoutePath.AccountSettingsGeneral;

    const tabPanelPadding = 'p-6';

    const tabs = [
        {
            label: UIStrings.GeneralSettingsTab,
            path: RoutePath.AccountSettingsGeneral,
            to: generatePath(RoutePath.AccountSettingsGeneral, { companyId }),
        },
        {
            label: UIStrings.CompanyProfile,
            path: RoutePath.AccountSettingsCompanyProfile,
            to: generatePath(RoutePath.AccountSettingsCompanyProfile, { companyId }),
        },
        {
            label: UIStrings.Notifications,
            path: RoutePath.AccountSettingsNotifications,
            to: generatePath(RoutePath.AccountSettingsNotifications, { companyId }),
        },
        {
            label: UIStrings.PaymentSettingsTab,
            path: RoutePath.AccountSettingsPayment,
            to: generatePath(RoutePath.AccountSettingsPayment, { companyId }),
        },
        {
            label: UIStrings.EditEmailTemplateSettingsTab,
            path: RoutePath.AccountSettingsEmailTemplates,
            to: generatePath(RoutePath.AccountSettingsEmailTemplates, { companyId }),
        },
        {
            label: UIStrings.TeamMembers,
            path: RoutePath.AccountSettingsTeamMembers,
            to: generatePath(RoutePath.AccountSettingsTeamMembers, { companyId }),
        },
        {
            label: UIStrings.Integrations,
            path: RoutePath.AccountSettingsIntegrations,
            to: generatePath(RoutePath.AccountSettingsIntegrations, { companyId }),
        },
    ];

    return (
        <Card className="mt-8 rounded-xl border drop-shadow-none">
            <Tabs value={currentTab} className="gap-0">
                <TabsList
                    variant="line-primary"
                    className="w-full min-w-0 flex-nowrap justify-start overflow-x-auto rounded-t-xl border-b px-3 md:px-6"
                    aria-label={UIStrings.AccountSettings}
                >
                    {tabs.map((tab) => (
                        <TabsTrigger key={tab.path} value={tab.path} asChild>
                            <Link to={tab.to}>{tab.label}</Link>
                        </TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value={RoutePath.AccountSettingsGeneral}>
                    <CardContent className={tabPanelPadding}>
                        <ProfileInfo />
                    </CardContent>
                </TabsContent>
                <TabsContent value={RoutePath.AccountSettingsCompanyProfile}>
                    <CardContent className={tabPanelPadding}>
                        <CompanyProfileTab />
                    </CardContent>
                </TabsContent>
                <TabsContent value={RoutePath.AccountSettingsNotifications}>
                    <CardContent className={tabPanelPadding}>
                        <AccountNotifications />
                    </CardContent>
                </TabsContent>
                <TabsContent value={RoutePath.AccountSettingsPayment}>
                    <CardContent className={tabPanelPadding}>
                        <PaymentTab />
                    </CardContent>
                </TabsContent>
                <TabsContent value={RoutePath.AccountSettingsEmailTemplates}>
                    <CardContent className={tabPanelPadding}>
                        <EditEmailTemplate />
                    </CardContent>
                </TabsContent>
                <TabsContent value={RoutePath.AccountSettingsTeamMembers}>
                    <CardContent className={tabPanelPadding}>
                        <TeamMembersTab />
                    </CardContent>
                </TabsContent>
                <TabsContent value={RoutePath.AccountSettingsIntegrations}>
                    <CardContent className={tabPanelPadding}>
                        <IntegrationsTab />
                    </CardContent>
                </TabsContent>
            </Tabs>
        </Card>
    );
}

export default AccountSettingsTabs;
